import { IntegrationsTracking } from "@/lib/tracking/integrations";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  LoadingButton,
  RadioGroup,
  RadioGroupItem,
  Stack,
  toast,
  Typography,
} from "@altimateai/lego";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postIntegration } from "@/helpers/apis";
import { Link, useNavigate } from "react-router-dom";
import { GET_INTEGRATIONS } from "@/pages/integrations/constants";
import { useTenantInfo } from "@/helpers/hooks/useTenantInfo";
import { useBreadcrumb } from "@/components/breadcrumbs";

const addDbtIntegrationFormSchema = z.object({
  name: z
    .string({ message: "Integration name is required" })
    .min(3, { message: "Integration name must be at least 3 characters long" }),
  integration_type: z.enum(["dbt_core", "dbt_cloud"], { message: "Integration type is required" }),
  environment: z
    .string({ message: "Environment name is required" })
    .min(3, { message: "Environment name must be at least 3 characters long" }),
});

export const AddIntegration = () => {
  useBreadcrumb("New", {
    defaultParents: [
      { label: "Settings", pathname: "/settings/datastores" },
      { label: "Integrations", pathname: "/settings/integrations" },
    ],
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { rbacEnabled } = useTenantInfo();

  const form = useForm<z.infer<typeof addDbtIntegrationFormSchema>>({
    resolver: zodResolver(addDbtIntegrationFormSchema),
    defaultValues: {
      name: "",
      integration_type: "dbt_core",
      environment: "",
    },
  });

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: postIntegration,
    onSuccess: data => {
      IntegrationsTracking.created(form.getValues("integration_type"));
      if (rbacEnabled) {
        toast({
          title: "Successfully created integration",
          description:
            "To make this integration visible, assign it to a team by creating an access rule in the Teams page",
          action: (
            <Button asChild size="xs">
              <Link to="/usergroups">Go to Teams</Link>
            </Button>
          ),
        });
      }
      queryClient.invalidateQueries({ queryKey: [GET_INTEGRATIONS] });
      // open integrations models if successfully saved
      if (data?.dbt_core_integration_id && form.getValues("environment")) {
        navigate(
          `/settings/integrations/${data.dbt_core_integration_id}/${form.getValues("environment")}`
        );
      } else {
        navigate("/settings/integrations");
      }
    },
    onError: (e: string) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: e,
      });
    },
  });

  const onSubmit = (values: z.infer<typeof addDbtIntegrationFormSchema>) => {
    mutate(values);
  };

  return (
    <Card className={"al-w-1/3"}>
      <CardHeader>
        <Typography variant="page-heading">Add New Integration</Typography>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Integration Name" {...field} />
                  </FormControl>
                  <FormDescription>Unique name of the Integration</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="integration_type"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Integration Type</FormLabel>
                  <FormControl>
                    <RadioGroup
                      className={"!al-flex !al-flex-row !al-gap-2 !al-w-full"}
                      orientation={"horizontal"}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <Stack
                        className={"p-3 bg-white al-rounded-md al-border al-w-1/2"}
                        justify={"start"}
                        align={"center"}
                      >
                        <RadioGroupItem value="dbt_core" />
                        <Stack direction={"column"} gap={"sm"}>
                          <FormLabel className={"al-m-0"}>dbt Core</FormLabel>
                        </Stack>
                      </Stack>
                      <Stack
                        className={"p-3 bg-white al-rounded-md al-border al-w-1/2"}
                        justify={"start"}
                        align={"center"}
                      >
                        <RadioGroupItem value="dbt_cloud" />
                        <Stack direction={"column"} gap={"sm"}>
                          <FormLabel className={"al-m-0"}>dbt Cloud</FormLabel>
                        </Stack>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>Type of Integration</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="environment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environment Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Environment Name" {...field} />
                  </FormControl>
                  <FormDescription>Environment Name like PROD</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Stack justify={"start"} align={"center"} gap={"md"}>
              <Button variant={"outline"} onClick={() => navigate("/settings/integrations")}>
                Cancel
              </Button>
              <LoadingButton loading={isLoading} type="submit">
                Save
              </LoadingButton>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
